/**
 * The CodeMirror-FREE half of the code-editor foundation: the language union and the beautifier.
 *
 * 🚨 Nothing in this file may import `@codemirror/*`. It is reached by STATIC imports from
 * `ft-code-mirror.component.ts`, `code-editor.component.ts` and `ft-code-field.component.ts`, all
 * of which are declared in eagerly-loaded NgModules — so anything here lands in `main.js`. Every
 * CodeMirror import lives in `code-mirror.setup.ts`, behind the single dynamic `import()` in the
 * component, which is what keeps the editor out of the eager bundle entirely.
 */

/**
 * Editor syntax modes, and the complete set of them the app uses:
 *   css        — 19 `ft-code-editor` instances (touchpoint styling, domains URL mappings,
 *                mission-control branding) plus the contest / touchpoint CSS field editors
 *   json       — 3 `ft-code-editor` instances (third-party integration configuration, …)
 *   javascript — the `type: 'js'` field editors (friend-landing JSON settings, quote-list JSON,
 *                homepage advanced properties) — all of which actually hold JSON, see
 *                `syntaxDiagnostics`
 *   html       — the source-code toggle inside `ft-text-html-editor`
 *
 * There is deliberately no `typescript`: no template ever passed it, and the Monaco path for it was
 * broken anyway (`angular.json` copied `vs/language/typescript/tsMode.js` but never its 4.6 MB
 * `tsWorker.js`, so selecting it 404'd instead of producing an editor).
 */
export type CodeMirrorLanguage = 'html' | 'css' | 'json' | 'javascript';

/**
 * Beautify through the `window.js_beautify` / `window.css_beautify` globals that `vendor-globals.ts`
 * installs. Same source, same guard and same language split as the Ace editors used, so a
 * reformatted document is byte-identical to what they produced. Unavailable beautifier = no-op.
 *
 * This is now the app's ONLY formatting path. Monaco's `formatOnPaste` / `formatOnType` are gone
 * with Monaco, and its CSS formatting provider had already been removed with `css-format-monaco`
 * (FTK-7503) — which is the same `css_beautify` this calls, just reached directly.
 */
export function beautifyCode(code: string, language: CodeMirrorLanguage): string {
    try {
        const w = window as any;
        if (language === 'css' && typeof w.css_beautify === 'function') {
            return w.css_beautify(code);
        }
        if ((language === 'json' || language === 'javascript') && typeof w.js_beautify === 'function') {
            return w.js_beautify(code);
        }
    } catch {
        /* beautify unavailable — return as-is (matches the Ace editors' guarded behaviour). */
    }
    return code;
}
